import api from '@/services/apiService';
import { InvoicesApiResponse, MabarTransactionsApiResponse } from '@/types/invoice';
import { InvoiceDetailResponse } from '@/types/invoiceDetail';

export interface TransactionApiResponse {
  data: {
    wallet_id: string;
    transaction_type: string;
    amount: number;
    balance_before: number;
    balance_after: number;
    reference_type: string;
    reference_id: string;
    description: string;
    metadata: any;
    created_at: string;
    status: string;
  }[];
  message: string;
  pagination: {
    current_page: number;
    limit: number;
    offset: number;
    total: number;
    total_pages: number;
  };
  success: boolean;
}

export interface InvoiceStatsData {
  total_invoices: number;
  total_amount: number;
  total_paid: number;
  total_remaining: number;
  status_breakdown?: {
    status: string;
    count: number;
    amount: number;
  }[];
}

export interface ArenaInvoiceApiFilters {
  status?: string;
  order_type?: string;
  transaction_type?: string;
  payment_method?: string;
  customer_name?: string;
  date_from?: string;
  date_to?: string;
}

class InvoiceService {
  /**
   * Fetch invoices for a specific arena
   * @param arenaSlug - The slug of the arena
   * @param page - Page number (default: 1)
   * @param limit - Number of items per page (default: 25)
   * @returns Promise with invoices data
   */
  async getArenaInvoices(
    arenaSlug: string,
    page: number = 1,
    limit: number = 25,
    sortBy?: string,
    order?: 'asc' | 'desc',
    filters?: ArenaInvoiceApiFilters
  ): Promise<InvoicesApiResponse> {
    const params: Record<string, any> = {
      offset: (page - 1) * limit,
      limit,
      sort_by: sortBy,
      order,
    };
    if (filters?.status) params.status = filters.status;
    if (filters?.order_type) params.order_type = filters.order_type;
    if (filters?.transaction_type) params.transaction_type = filters.transaction_type;
    if (filters?.payment_method) params.payment_method = filters.payment_method;
    if (filters?.customer_name) params.customer_name = filters.customer_name;
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;

    const response = await api.get(`/arenas/${arenaSlug}/invoices`, {
      params,
    });

    return response.data;
  }

  async getInvoicesByCustomer(
    page: number = 1,
    limit: number = 10,
    sortBy?: string,
    order?: 'asc' | 'desc'
  ): Promise<InvoicesApiResponse> {
    const response = await api.get(`/client/transactions`, {
      params: {
        offset: (page - 1) * limit,
        limit,
        sort_by: sortBy,
        order,
      },
    });

    return response.data;
  }

  /**
   * Fetch online transactions for a specific arena (for wallet transactions table)
   * @param arenaSlug - The slug of the arena
   * @param page - Page number (default: 1)
   * @param limit - Number of items per page (default: 10)
   * @returns Promise with online transactions data
   */
  async getArenaOnlineTransactions(
    page: number = 1,
    limit: number = 10,
    sortBy?: string,
    order?: string
  ): Promise<TransactionApiResponse> {
    const response = await api.get(`/wallet/transactions`, {
      params: {
        offset: (page - 1) * limit,
        limit,
        sort_by: sortBy,
        order,
      },
    });

    return response.data;
  }

  /**
   * Fetch invoice detail for a specific invoice
   * @param arenaSlug - The slug of the arena
   * @param invoiceNumber - The invoice number
   * @returns Promise with invoice detail data
   */
  async getInvoiceDetail(arenaSlug: string, invoiceNumber: string): Promise<InvoiceDetailResponse> {
    const response = await api.get(`/arenas/${arenaSlug}/invoices/${invoiceNumber}`);
    return response.data;
  }

  async getInvoiceStats(arenaSlug: string): Promise<InvoiceStatsData | null> {
    try {
      const response = await api.get<{ success: boolean; data: InvoiceStatsData }>(
        `/arenas/${arenaSlug}/invoices/stats`
      );
      return response.data?.data || null;
    } catch {
      return null;
    }
  }

  async getInvoiceDetailClient(invoiceNumber: string): Promise<InvoiceDetailResponse> {
    const response = await api.get(`/client/invoice/${invoiceNumber}`);
    return response.data;
  }

  /**
   * Download invoice PDF for a specific invoice
   * @param arenaSlug - arena slug
   * @param invoiceNumber - invoice number
   * @returns Blob of PDF
   */
  async downloadInvoicePdf(arenaSlug: string, invoiceNumber: string): Promise<Blob> {
    const response = await api.get(`/arenas/${arenaSlug}/invoices/${invoiceNumber}/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async downloadClientInvoicePdf(invoiceNumber: string): Promise<Blob> {
    const response = await api.get(`/client/invoice/${invoiceNumber}/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async cancelOrder(orderNumber: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/client/orders/cancel`, {
      order_number: orderNumber,
      cancel_reason: 'Cancelled by user',
      rerefund_requested: false,
    });

    return {
      success: response.data.success || response.status === 200,
      message: response.data.message || 'Order cancelled successfully',
    };
  }

  /**
   * Format currency amount to Indonesian Rupiah
   * @param amount - Amount in number
   * @returns Formatted string
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Format date to Indonesian format
   * @param dateString - ISO date string
   * @returns Formatted date string
   */
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }

  /**
   * Update invoice payment status
   * @param arenaSlug - The slug of the arena
   * @param invoiceNumber - The invoice number
   * @param paymentStatus - New payment status (PAID, PENDING, CANCELLED, etc.)
   * @returns Promise with update result
   */
  async updateInvoiceStatus(
    arenaSlug: string,
    invoiceNumber: string,
    paymentStatus: string,
    paymentMethod?: string
  ): Promise<{ success: boolean; message: string }> {
    const payload: Record<string, any> = {
      invoice_number: invoiceNumber,
      payment_status: paymentStatus,
    };
    if (paymentMethod) {
      payload.payment_method = paymentMethod;
    }
    const response = await api.put(`/arenas/${arenaSlug}/invoices/status`, payload);

    return {
      success: response.data.success || response.status === 200,
      message: response.data.message || 'Invoice status updated successfully',
    };
  }

  async cancelPaidInvoice(
    arenaSlug: string,
    invoiceNumber: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post(
      `/arenas/${arenaSlug}/cancel-order-invoice?invoice-number=${invoiceNumber}`,
      {
        cancel_reason: 'Cancelled by admin',
      }
    );

    return {
      success: response.data.success || response.status === 200,
      message: response.data.message || 'Paid invoice cancelled successfully',
    };
  }

  /**
   * Delete invoice/transaction
   * @param arenaSlug - The slug of the arena
   * @param invoiceNumber - The invoice number to delete
   * @returns Promise with delete result
   */
  async deleteInvoice(
    arenaSlug: string,
    invoiceNumber: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/arenas/${arenaSlug}/invoices/${invoiceNumber}`);

    return {
      success: response.data.success || response.status === 200,
      message: response.data.message || 'Invoice deleted successfully',
    };
  }

  async getClientMabarTransactions(
    page: number = 1,
    limit: number = 10,
    search?: string,
    sortBy?: string,
    order?: 'asc' | 'desc'
  ): Promise<MabarTransactionsApiResponse> {
    const response = await api.get(`/client/mabar-transactions`, {
      params: {
        page,
        limit,
        search,
        sort_by: sortBy,
        sort_order: order,
      },
    });

    return response.data;
  }
}

// Export singleton instance
export const invoiceService = new InvoiceService();
export default invoiceService;
